import { ComponentType, useLayoutEffect } from 'react';
import { useAppContext } from '../../contexts/AppContext';
import HeaderContent from './Header';
import { useAuthContext } from '../../contexts/AuthContext';
import { usePlanAccess } from '../../plans/usePlanAccess';
import LockedFeaturePage from '../../plans/LockedFeaturePage';
import type { SalonFlowFeatureKey } from '../../plans/salonflowPlans';
import { useModuleSheet } from './useModuleSheet';

function BlankActions() {
    return <></>;
}

interface ContentProps {
    title: string;
    Element: ComponentType;
    Actions: ComponentType | any;
    Provider?: any;
    returnHome?: boolean;
    returnTo?: string;
    planFeature?: SalonFlowFeatureKey;
    hideHeader?: boolean;
    actionTitles?: Record<string, string>;
    actionParents?: Record<string, string>;
    stickyHeader?: boolean;
}

export default function Content({
    title,
    Element,
    Actions,
    returnHome,
    Provider,
    returnTo,
    planFeature,
    hideHeader,
    actionTitles,
    actionParents,
    stickyHeader = true,
}: ContentProps) {
    const { isScrolled, setSubMenu } = useAppContext();
    const { user } = useAuthContext();
    const { restrictionsApply, hasFeature, requiredUpgrade } = usePlanAccess();
    const moduleSheet = useModuleSheet();

    const blockedByPlan = !!(
        restrictionsApply &&
        planFeature &&
        user &&
        !hasFeature(planFeature)
    );

    useLayoutEffect(() => {
        if (blockedByPlan) {
            setSubMenu(false);
        }
    }, [blockedByPlan, setSubMenu]);

    // Titulo grande no topo; ao rolar, vira barra sticky compacta com material translucido.
    // Com `stickyHeader` false, o cabecalho rola junto com a pagina.
    const condensed = stickyHeader && isScrolled;
    // Sem o header global, a tela de plano bloqueado precisa do cabecalho para ter titulo e voltar.
    const showHeader = blockedByPlan ? moduleSheet : !hideHeader;
    const header = showHeader ? (
        <div className={`sf-content-header ${stickyHeader ? 'is-sticky' : ''} ${condensed ? 'is-condensed' : ''}`}>
            <div className="sf-toolbar-shell">
                <HeaderContent
                    title={title}
                    Actions={blockedByPlan ? BlankActions : Actions}
                    returnHome={returnHome}
                    returnTo={returnTo}
                    actionTitles={actionTitles}
                    actionParents={actionParents}
                    condensed={condensed}
                />
            </div>
        </div>
    ) : null;

    const pageBody = blockedByPlan && planFeature ? (
        <LockedFeaturePage minimumPlan={requiredUpgrade(planFeature)} sectionTitle={title} />
    ) : (
        <Element />
    );

    return (
        <div>
            {!!Provider ? (
                <Provider>
                    {header}
                    {pageBody}
                </Provider>
            ) : (
                <>
                    {header}
                    {pageBody}
                </>
            )}
        </div>
    );
}
